import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { NavLink } from "react-router-dom";
import { cn, initials } from "../../lib/utils";
import { t } from "../../i18n";

/* --------------------------------- Logo ---------------------------------- */

export function Logo({ compact = false, size = 32 }: { compact?: boolean; size?: number }) {
  return (
    <span className={cn("logo", compact && "logo-compact")} style={{ "--logo-size": `${size}px` } as CSSProperties}>
      <span className="logo-mark" aria-hidden="true">
        <svg viewBox="0 0 64 64" width={size} height={size}>
          <rect width="64" height="64" rx="16" fill="currentColor" opacity="0.08" />
          <path d="M15 18v28M15 32l14-14M15 32l15 14" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx="46" cy="19" r="4" fill="var(--accent)" />
          <path d="M40 45c4-7 8-7 12 0" stroke="var(--accent)" strokeWidth="3" fill="none" strokeLinecap="round" />
        </svg>
      </span>
      {!compact && (
        <span className="logo-word">
          <strong>KORIN</strong>
          <em>STUDIO</em>
        </span>
      )}
    </span>
  );
}

/* -------------------------------- Button --------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "outline" | "danger" | "subtle";
type ButtonSize = "xs" | "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", loading, icon, iconRight, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn("btn", `btn-${variant}`, `btn-${size}`, className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="spin" size={size === "xs" ? 13 : 15} /> : icon}
      {children}
      {iconRight}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  function IconButton({ label, className, variant = "ghost", size = "icon", children, ...props }, ref) {
    return (
      <Tooltip content={label}>
        <button
          ref={ref}
          aria-label={label}
          className={cn("btn", `btn-${variant}`, `btn-${size}`, "btn-icon", className)}
          {...props}
        >
          {children}
        </button>
      </Tooltip>
    );
  },
);

/* -------------------------------- Badge ---------------------------------- */

export function Badge({
  children,
  variant = "default",
  dot,
  color,
  className,
  style,
}: {
  children: ReactNode;
  variant?: "default" | "accent" | "success" | "warning" | "danger" | "violet" | "outline" | "muted";
  dot?: boolean;
  color?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={cn("badge", `badge-${variant}`, className)}
      style={{ ...(color ? { "--badge-color": color } : {}), ...style } as CSSProperties}
    >
      {dot && <span className="badge-dot" style={color ? { background: color } : undefined} />}
      {children}
    </span>
  );
}

export function StatusDot({ color, pulse = false, size = 7 }: { color: string; pulse?: boolean; size?: number }) {
  return <span className={cn("status-dot", pulse && "is-pulse")} style={{ background: color, width: size, height: size }} />;
}

/* -------------------------------- Avatar --------------------------------- */

export function Avatar({
  name,
  src,
  color = "#f0a93b",
  size = 32,
  className,
  ring = false,
  title,
}: {
  name: string;
  src?: string;
  color?: string;
  size?: number;
  className?: string;
  ring?: boolean;
  title?: string;
}) {
  return (
    <AvatarPrimitive.Root
      className={cn("avatar", ring && "avatar-ring", className)}
      style={{ width: size, height: size, "--avatar-color": color, fontSize: size * 0.34 } as CSSProperties}
      title={title ?? name}
    >
      {src && <AvatarPrimitive.Image className="avatar-image" src={src} alt={name} />}
      <AvatarPrimitive.Fallback className="avatar-fallback">{initials(name)}</AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export function AvatarStack({
  people,
  max = 4,
  size = 26,
}: {
  people: { id: string; name: string; avatarColor?: string; avatarUrl?: string }[];
  max?: number;
  size?: number;
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="avatar-stack">
      {shown.map((person, index) => (
        <span key={person.id} style={{ zIndex: max - index, marginLeft: index === 0 ? 0 : -8 }}>
          <Avatar name={person.name} src={person.avatarUrl} color={person.avatarColor ?? "#f0a93b"} size={size} ring />
        </span>
      ))}
      {rest > 0 && <span className="avatar-more">+{rest}</span>}
    </span>
  );
}

/* --------------------------------- Modal --------------------------------- */

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  hideClose?: boolean;
}

export function Modal({ open, onOpenChange, title, description, children, footer, size = "md", className, hideClose }: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="modal-overlay" />
        <DialogPrimitive.Content className={cn("modal", `modal-${size}`, className)}>
          <div className="modal-head">
            <div>
              <DialogPrimitive.Title className="modal-title">{title}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="modal-description">{description}</DialogPrimitive.Description>
              )}
            </div>
            {!hideClose && (
              <DialogPrimitive.Close className="modal-close" aria-label={t("Закрыть")}>
                <X size={17} />
              </DialogPrimitive.Close>
            )}
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = t("Подтвердить"),
  onConfirm,
  danger,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  danger?: boolean;
  loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Отмена")}
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="confirm-spacer" />
    </Modal>
  );
}

/* ------------------------------- Dropdown -------------------------------- */

export const Menu = DropdownPrimitive.Root;
export const MenuTrigger = DropdownPrimitive.Trigger;
export const MenuGroup = DropdownPrimitive.Group;
export const MenuPortal = DropdownPrimitive.Portal;
export const MenuSub = DropdownPrimitive.Sub;
export const MenuSubTrigger = DropdownPrimitive.SubTrigger;
export const MenuSubContent = DropdownPrimitive.SubContent;

export function MenuContent({
  children,
  align = "end",
  sideOffset = 6,
  className,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content className={cn("menu-content", className)} align={align} sideOffset={sideOffset}>
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}

export function MenuItem({
  children,
  icon,
  onSelect,
  danger,
  disabled,
  shortcut,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onSelect?: (event: Event) => void;
  danger?: boolean;
  disabled?: boolean;
  shortcut?: string;
}) {
  return (
    <DropdownPrimitive.Item
      className={cn("menu-item", danger && "is-danger")}
      onSelect={onSelect}
      disabled={disabled}
    >
      {icon && <span className="menu-icon">{icon}</span>}
      <span className="menu-label">{children}</span>
      {shortcut && <kbd className="menu-shortcut">{shortcut}</kbd>}
    </DropdownPrimitive.Item>
  );
}

export function MenuSeparator() {
  return <DropdownPrimitive.Separator className="menu-separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownPrimitive.Label className="menu-label-header">{children}</DropdownPrimitive.Label>;
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export function PopoverContent({
  children,
  align = "end",
  className,
  sideOffset = 8,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  sideOffset?: number;
}) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content className={cn("popover-content", className)} align={align} sideOffset={sideOffset}>
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

/* -------------------------------- Tooltip -------------------------------- */

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <TooltipPrimitive.Provider delayDuration={350}>{children}</TooltipPrimitive.Provider>;
}

export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "right" | "bottom" | "left" }) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="tooltip-content" side={side} sideOffset={6}>
          {content}
          <TooltipPrimitive.Arrow className="tooltip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ---------------------------------- Tabs --------------------------------- */

export const Tabs = TabsPrimitive.Root;
export const TabsList = forwardRef<HTMLDivElement, TabsPrimitive.TabsListProps & { className?: string }>(
  function TabsList({ className, ...props }, ref) {
    return <TabsPrimitive.List ref={ref} className={cn("tabs-list", className)} {...props} />;
  },
);
export const TabsTrigger = forwardRef<HTMLButtonElement, TabsPrimitive.TabsTriggerProps & { className?: string }>(
  function TabsTrigger({ className, ...props }, ref) {
    return <TabsPrimitive.Trigger ref={ref} className={cn("tabs-trigger", className)} {...props} />;
  },
);
export const TabsContent = TabsPrimitive.Content;

/* -------------------------------- Switch --------------------------------- */

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  label,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <span className="switch-row">
      <SwitchPrimitive.Root
        className="switch"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
      >
        <SwitchPrimitive.Thumb className="switch-thumb" />
      </SwitchPrimitive.Root>
      {label && <span className="switch-label">{label}</span>}
    </span>
  );
}

/* ------------------------------- Progress -------------------------------- */

export function Progress({ value, color, className }: { value: number; color?: string; className?: string }) {
  return (
    <ProgressPrimitive.Root className={cn("progress", className)} value={Math.max(0, Math.min(100, value))}>
      <ProgressPrimitive.Indicator
        className="progress-indicator"
        style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color ?? "var(--accent)" }}
      />
    </ProgressPrimitive.Root>
  );
}

/* --------------------------------- Inputs -------------------------------- */

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...props }, ref) {
    return <input ref={ref} className={cn("input", invalid && "is-invalid", className)} {...props} />;
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn("textarea", className)} {...props} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, style, ...props }, ref) {
    const wrapperStyle = style?.width
      ? ({ width: style.width, flex: `0 0 ${style.width}` } as CSSProperties)
      : undefined;
    return (
      <span className="select-wrap" style={wrapperStyle}>
        <select ref={ref} className={cn("select", className)} style={style} {...props}>
          {children}
        </select>
        <ChevronDown size={14} className="select-caret" />
      </span>
    );
  },
);

export function Field({
  label,
  hint,
  error,
  children,
  className,
  required,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  required?: boolean;
}) {
  return (
    <label className={cn("field", className)}>
      {label && (
        <span className="field-label">
          {label}
          {required && <em>*</em>}
        </span>
      )}
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

/* -------------------------------- Utility -------------------------------- */

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cn("spin", className)} />;
}

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span className={cn("skeleton", className)} style={style} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  compact,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cn("empty-state", compact && "is-compact")}>
      {icon && <div className="empty-icon">{icon}</div>}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  className,
  size = "md",
}: {
  items: { value: T; label: ReactNode; icon?: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <div className={cn("segmented", size === "sm" && "segmented-sm", className)} role="group">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          className={cn("segmented-item", value === item.value && "is-active")}
          onClick={() => onChange(item.value)}
          aria-pressed={value === item.value}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <div className={cn("divider", className)} />;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  children,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header-main">
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {children}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </header>
  );
}

export function SectionTitle({
  title,
  count,
  action,
  description,
}: {
  title: ReactNode;
  count?: number;
  action?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <div className="section-title">
      <div>
        <h2>
          {title}
          {count !== undefined && <span className="section-count">{count}</span>}
        </h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function NavLinkItem({
  to,
  icon,
  children,
  end,
  badge,
  className,
}: {
  to: string;
  icon?: ReactNode;
  children: ReactNode;
  end?: boolean;
  badge?: ReactNode;
  className?: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }: { isActive: boolean }) => cn("nav-item", isActive && "is-active", className)}
    >
      {icon}
      <span>{children}</span>
      {badge}
    </NavLink>
  );
}

export function CheckMark({ checked }: { checked: boolean }) {
  return <span className={cn("checkmark", checked && "is-checked")}>{checked && <Check size={11} />}</span>;
}

export function Tag({ children, className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("tag", className)} {...props}>{children}</span>;
}
